import dayjs, { Dayjs } from 'dayjs';
import { useIntl } from 'react-intl';
import { CalendarDayEvents, getInitials } from '../utils';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import { alpha, Avatar, Box, Chip, Divider, IconButton, Popover, Stack, Typography } from '@mui/material';

export type SelectedCalendarDay = {
  anchorElement: HTMLElement;
  date: Dayjs;
  events: CalendarDayEvents;
};

type DayDetailsPopoverProps = {
  selectedDay: SelectedCalendarDay | null;
  onClose: () => void;
};

const DayDetailsPopover = ({ selectedDay, onClose }: DayDetailsPopoverProps) => {
  const intl = useIntl();
  const dateLabel = selectedDay
    ? intl.formatDate(selectedDay.date.toDate(), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const formatTime = (value: string) => {
    const date = dayjs(value);

    return date.isValid()
      ? intl.formatTime(date.toDate(), {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
  };

  return (
    <Popover
      open={!!selectedDay}
      anchorEl={selectedDay?.anchorElement}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'yearly-holiday-day-details-title',
          sx: {
            width: 360,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'min(520px, calc(100vh - 32px))',
            mt: 0.75,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 8,
          },
        },
      }}>
      {selectedDay && (
        <Box>
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ p: 2, pb: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                width: 42,
                height: 42,
                borderRadius: 2,
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.14),
                color: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
              }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {selectedDay.date.date()}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {intl.formatMessage({ id: 'yearlyHoliday.dayDetails', defaultMessage: 'Gün detayı' })}
              </Typography>
              <Typography
                id="yearly-holiday-day-details-title"
                variant="subtitle1"
                sx={{ fontWeight: 750, textTransform: 'capitalize' }}>
                {dateLabel}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label={intl.formatMessage({ id: 'yearlyHoliday.closeDetails', defaultMessage: 'Detayları kapat' })}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Divider />

          <Stack spacing={1.5} sx={{ p: 2 }}>
            {selectedDay.events.holidays.map((holiday) => (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                key={`holiday-detail-${holiday.id}-${holiday.title}`}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.success.main, 0.3),
                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                }}>
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
                    backgroundColor: (theme) => alpha(theme.palette.success.main, 0.16),
                  }}>
                  <EventRoundedIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {intl.formatMessage({ id: 'yearlyHoliday.publicHoliday', defaultMessage: 'Resmi tatil' })}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {holiday.title}
                  </Typography>
                </Box>

                {holiday.isHalfDay && <Chip size="small" color="success" label="1/2" sx={{ fontWeight: 800 }} />}
              </Stack>
            ))}

            {selectedDay.events.permissions.length > 0 && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <BeachAccessRoundedIcon color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>
                    {intl.formatMessage(
                      { id: 'yearlyHoliday.leaveDetails', defaultMessage: 'İzinli ekip ({count})' },
                      { count: selectedDay.events.permissions.length }
                    )}
                  </Typography>
                </Stack>

                {selectedDay.events.permissions.map((permission) => (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1.25}
                    key={`permission-detail-${permission.assignedUserId}`}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.default',
                    }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        color: (theme) =>
                          theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
                        backgroundColor: (theme) => alpha(theme.palette.success.main, 0.15),
                      }}>
                      {getInitials(permission.nameSurname)}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 750 }}>
                          {permission.nameSurname}
                        </Typography>

                        {permission.isHalfDay && (
                          <Chip size="small" color="success" label="1/2" sx={{ height: 20, fontWeight: 800 }} />
                        )}

                        {permission.totalHours > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<AccessTimeRoundedIcon />}
                            label={`${intl.formatNumber(permission.totalHours, {
                              maximumFractionDigits: 2,
                            })} ${intl.formatMessage({ id: 'yearlyHoliday.hoursShort', defaultMessage: 'sa' })}`}
                            sx={{ height: 20 }}
                          />
                        )}
                      </Stack>

                      <Stack spacing={0.65} sx={{ mt: 0.75 }}>
                        {permission.entries.map((entry, entryIndex) => {
                          const entryStart = dayjs(entry.startDate);
                          const entryEnd = dayjs(entry.endDate);
                          const displayEnd =
                            entryEnd.isValid() &&
                            entryEnd.isAfter(entryStart) &&
                            entryEnd.valueOf() === entryEnd.startOf('day').valueOf()
                              ? entryEnd.subtract(1, 'millisecond')
                              : entryEnd;
                          const isSingleCalendarDay =
                            entryStart.isValid() && displayEnd.isValid() && entryStart.isSame(displayEnd, 'day');
                          const startTime = formatTime(entry.startDate);
                          const endTime = formatTime(entry.endDate);
                          const dateRange =
                            entryStart.isValid() && displayEnd.isValid() && !isSingleCalendarDay
                              ? `${intl.formatDate(entryStart.toDate(), {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}–${intl.formatDate(displayEnd.toDate(), {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}`
                              : '';

                          return (
                            <Box key={`${entry.id}-${entry.startDate}-${entryIndex}`} sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 650 }}>
                                {entry.activityTypeTitle ||
                                  intl.formatMessage({ id: 'yearlyHoliday.leave', defaultMessage: 'İzin' })}
                                {dateRange
                                  ? ` · ${dateRange}`
                                  : startTime && endTime
                                    ? ` · ${startTime}–${endTime}`
                                    : ''}
                              </Typography>

                              {!!entry.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                  {entry.description}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      )}
    </Popover>
  );
};

export default DayDetailsPopover;
